// Editor.jsx — Emergent build.
// Persistence is MongoDB via the backend (no Yjs/Hocuspocus). The editor loads
// the chapter HTML, autosaves on change (debounced), and polls for remote
// updates so multiple users converge (near-live, last-write-wins).
// Word-grade tracked changes (author/time/reason), rich paste, and Word export.
 
import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Image } from "@tiptap/extension-image";
import { Table, TableRow, TableCell, TableHeader } from "@tiptap/extension-table";
import { Extension } from "@tiptap/core";
import {
  Insertion, Deletion, AutoTrackPlugin, trackCtx,
  markDeletion, acceptAll, rejectAll,
} from "../lib/trackChanges.js";
import { RichPaste } from "../lib/richPaste.js";
import { api } from "../lib/api.js";
 
const AutoTrack = Extension.create({
  name: "autoTrack",
  addProseMirrorPlugins() { return [AutoTrackPlugin()]; },
});
 
export default function Editor({
  workspace, chapter, user, trackOn, setTrackOn, onEditorReady, registerExport, initialHtml,
}) {
  const [reason, setReason] = useState("");
  const [saved, setSaved] = useState("Loaded");
  const saveTimer = useRef(null);
  const lastRemote = useRef(initialHtml || "");
  const loadingRef = useRef(false);
 
  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({ inline: false, allowBase64: true }),
      Table.configure({ resizable: false, HTMLAttributes: { class: "drhp-table" } }),
      TableRow, TableHeader, TableCell,
      Insertion, Deletion, AutoTrack,
      RichPaste.configure({
        uploadImage: async (file) => {
          try { return await api.uploadImage(file); }
          catch { return await fileToDataUrl(file); }
        },
      }),
    ],
    editorProps: { attributes: { class: "drhp-editor" } },
    content: initialHtml || "<p></p>",
    immediatelyRender: false,
  });
 
  // Initial content arrives via the `initialHtml` prop (the parent fetches it
  // and remounts this component per chapter), so no setContent-on-load needed.
 
  // Keep track-change context in sync and route revisions to the backend.
  useEffect(() => {
    trackCtx.enabled = trackOn;
    trackCtx.user = { id: user.id, name: user.name };
    trackCtx.reason = reason;
    trackCtx.onRevision = (rev) => {
      api.recordRevision({
        chapter, workspace, userId: rev.authorId, userName: rev.author,
        type: rev.type, text: rev.text, reason: rev.reason, date: rev.date,
      }).catch(() => {});
    };
  }, [trackOn, user.id, user.name, reason, chapter, workspace]);
 
  // Debounced autosave on edits.
  useEffect(() => {
    if (!editor) return;
    const handler = () => {
      if (loadingRef.current) return;
      setSaved("Saving…");
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {
        const html = editor.getHTML();
        lastRemote.current = html;
        await api.saveDoc({ workspace, chapter, html, userId: user.id, userName: user.name });
        setSaved("Saved");
      }, 800);
    };
    editor.on("update", handler);
    return () => editor.off("update", handler);
  }, [editor, workspace, chapter, user.id, user.name]);
 
  // Poll for remote changes (other users) every 5s; apply only if the editor
  // isn't focused (avoid clobbering active typing).
  useEffect(() => {
    if (!editor) return;
    const iv = setInterval(async () => {
      if (editor.isFocused || loadingRef.current) return;
      try {
        const { html } = await api.getDoc(workspace, chapter);
        if (html && html !== lastRemote.current && html !== editor.getHTML()) {
          lastRemote.current = html;
          const prev = trackCtx.enabled;
          trackCtx.enabled = false;
          editor.commands.setContent(html, { emitUpdate: false });
          trackCtx.enabled = prev;
        }
      } catch {}
    }, 5000);
    return () => clearInterval(iv);
  }, [editor, workspace, chapter]);
 
  useEffect(() => { if (editor && onEditorReady) onEditorReady(editor); }, [editor, onEditorReady]);
  useEffect(() => { if (registerExport) registerExport(() => handleExport); }, [editor]);
 
  async function handleExport() {
    const html = editor.getHTML();
    const body = {
      chapter, workspace, html, title: `DRHP — ${chapter}`,
      creator: user.name, userId: user.id, userName: user.name,
    };
    let result = await api.exportDocx(body);
    if (result.blocked) {
      const msg = result.issues.map((i) => `• [${i.severity}] ${i.message}`).join("\n");
      if (!window.confirm(`Filing validation:\n\n${msg}\n\nExport anyway?`)) return;
      result = await api.exportDocx(body, true);
    }
    if (result.blob) {
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${chapter}.docx`; a.click();
      URL.revokeObjectURL(url);
    }
  }
 
  function doDelete() {
    const r = reason || window.prompt("Reason for deletion (optional):") || "";
    markDeletion(editor, user, r);
  }
 
  if (!editor) return <div className="doc-area"><div style={{ padding: 20 }}>Loading editor…</div></div>;
 
  return (
    <div className="doc-area">
      <div className="editor-bar">
        <div className="eb-group">
          <button className="eb-btn" title="Undo" onClick={() => editor.chain().focus().undo().run()}>↶</button>
          <button className="eb-btn" title="Redo" onClick={() => editor.chain().focus().redo().run()}>↷</button>
        </div>
        <div className="eb-group">
          <button className={`eb-btn ${editor.isActive("bold") ? "on" : ""}`} title="Bold"
            onClick={() => editor.chain().focus().toggleBold().run()}><b>B</b></button>
          <button className={`eb-btn ${editor.isActive("italic") ? "on" : ""}`} title="Italic"
            onClick={() => editor.chain().focus().toggleItalic().run()}><i>I</i></button>
          <button className={`eb-btn ${editor.isActive("heading", { level: 2 }) ? "on" : ""}`} title="Heading"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</button>
          <button className={`eb-btn ${editor.isActive("bulletList") ? "on" : ""}`} title="List"
            onClick={() => editor.chain().focus().toggleBulletList().run()}>≔</button>
          <button className="eb-btn" title="Insert table"
            onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>⊞</button>
        </div>
        <div className="eb-group">
          <button className={`eb-pill ${trackOn ? "track-on" : ""}`}
            onClick={() => setTrackOn(!trackOn)}
            title="When on, every edit is attributed to you with time + reason">
            ✎ Track changes: {trackOn ? "on" : "off"}
          </button>
          <button className="eb-btn" title="Mark selection deleted" disabled={!trackOn}
            onClick={doDelete}>−del</button>
          <button className="eb-btn" title="Accept all changes" onClick={() => acceptAll(editor)}>✓</button>
          <button className="eb-btn" title="Reject all changes" onClick={() => rejectAll(editor)}>✕</button>
        </div>
        <span className="dm-saved" style={{ marginLeft: "auto" }}>☁ {saved}</span>
      </div>
 
      {trackOn && (
        <div className="reason-bar">
          <span className="reason-lbl">Reason for next change:</span>
          <input className="reason-input" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. updated per auditor's restated FY25 figures (optional)" />
          {reason && <button className="reason-clear" onClick={() => setReason("")}>clear</button>}
        </div>
      )}
 
      <div className="doc-head">
        <span className="dm-badge">Editing as {user.name}</span>
        <span className="paste-hint">Paste from Word / Excel / images supported</span>
      </div>
 
      <EditorContent editor={editor} />
    </div>
  );
}
 
function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}
 